import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { articles, hotKeywords } from "@/mocks/articles";
import { usePageMeta } from "@/hooks/usePageMeta";
import ArticleCard from "@/components/base/ArticleCard";
import Pagination from "@/components/base/Pagination";

const PAGE_SIZE = 6;

const typeFilters = [
  { value: "all", label: "全部" },
  { value: "news", label: "新闻" },
  { value: "video", label: "视频" },
  { value: "report", label: "专项报道" },
];

export default function SearchPage() {
  const [params, setParams] = useSearchParams();
  const urlQ = params.get("q") || "";
  const [input, setInput] = useState(urlQ);
  const [type, setType] = useState("all");
  const [page, setPage] = useState(1);

  useEffect(() => {
    setInput(urlQ);
    setPage(1);
  }, [urlQ]);

  usePageMeta({
    title: urlQ ? `搜索“${urlQ}” - 吃瓜网站` : "搜索资讯 - 吃瓜网站",
    description: urlQ
      ? `在吃瓜网站搜索“${urlQ}”的相关娱乐资讯，包含明星动态、影视评论与热点八卦。`
      : "在吃瓜网站搜索你感兴趣的明星、影视、热点与八卦资讯。",
    keywords: `吃瓜网站,娱乐搜索,${urlQ || "娱乐资讯"}`,
  });

  const results = useMemo(() => {
    const q = urlQ.trim().toLowerCase();
    if (!q) return [];
    return articles
      .filter((a) => {
        const haystack = [a.title, a.summary, a.categoryName, ...a.tags, ...a.content].join(" ").toLowerCase();
        return haystack.includes(q);
      })
      .filter((a) => (type === "all" ? true : a.type === type))
      .sort((a, b) => new Date(b.publishTime).getTime() - new Date(a.publishTime).getTime());
  }, [urlQ, type]);

  const totalPages = Math.max(1, Math.ceil(results.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const paged = results.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const submit = (value: string) => {
    const next = value.trim();
    if (next) setParams({ q: next });
    else setParams({});
    setPage(1);
  };

  return (
    <div className="w-full px-4 md:px-6 py-6 md:py-8">
      <h1 className="text-2xl md:text-3xl font-extrabold text-foreground-950">搜索资讯</h1>
      <p className="mt-2 text-sm text-foreground-500">输入关键词，快速找到你感兴趣的娱乐内容</p>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          submit(input);
        }}
        className="mt-5 flex flex-col sm:flex-row gap-2 sm:gap-3"
      >
        <div className="relative flex-1">
          <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-foreground-400" />
          <input
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="搜索明星、影视、八卦、热点..."
            className="w-full h-12 pl-11 pr-4 rounded-full bg-background-100 border border-background-200 text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-300"
          />
        </div>
        <button
          type="submit"
          className="h-12 px-7 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 whitespace-nowrap flex items-center justify-center gap-2 transition-colors"
        >
          <i className="ri-search-line" />
          搜索
        </button>
      </form>

      {urlQ && (
        <div className="mt-6 flex items-center flex-wrap gap-2">
          <span className="text-sm text-foreground-500 mr-1">内容类型：</span>
          {typeFilters.map((t) => (
            <button
              key={t.value}
              type="button"
              onClick={() => {
                setType(t.value);
                setPage(1);
              }}
              className={`px-4 py-1.5 rounded-full text-sm whitespace-nowrap transition-colors ${
                type === t.value
                  ? "bg-primary-500 text-background-50 font-medium"
                  : "bg-background-100 text-foreground-600 hover:bg-background-200"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
      )}

      {urlQ ? (
        <div className="mt-6">
          <p className="text-sm text-foreground-500 mb-4">
            关于“<span className="text-primary-600 font-semibold">{urlQ}</span>”共找到{" "}
            <span className="text-primary-600 font-semibold">{results.length}</span> 条结果
          </p>

          {paged.length > 0 ? (
            <>
              <div className="space-y-3 md:space-y-4">
                {paged.map((a) => (
                  <ArticleCard key={a.id} article={a} />
                ))}
              </div>
              <Pagination current={currentPage} total={totalPages} onChange={setPage} />
            </>
          ) : (
            <div className="py-16 text-center rounded-lg border border-dashed border-background-300 bg-background-50">
              <i className="ri-search-eye-line text-5xl text-foreground-300" />
              <h3 className="mt-4 text-lg font-bold text-foreground-900">没有找到相关结果</h3>
              <p className="mt-2 text-sm text-foreground-500">换个关键词试试，或看看大家都在搜什么</p>
              <div className="mt-5 flex flex-wrap justify-center gap-2 max-w-xl mx-auto">
                {hotKeywords.map((w) => (
                  <button
                    key={w}
                    type="button"
                    onClick={() => submit(w)}
                    className="px-3 py-1.5 rounded-full bg-background-100 text-xs md:text-sm text-foreground-600 hover:bg-secondary-500 hover:text-background-50 whitespace-nowrap transition-colors"
                  >
                    {w}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="mt-8">
          <h2 className="text-base font-bold text-foreground-900 mb-3 flex items-center gap-2">
            <i className="ri-fire-line text-accent-500" />
            大家都在搜
          </h2>
          <div className="flex flex-wrap gap-2">
            {hotKeywords.map((w) => (
              <button
                key={w}
                type="button"
                onClick={() => submit(w)}
                className="px-3.5 py-2 rounded-full bg-background-100 text-sm text-foreground-600 hover:bg-secondary-500 hover:text-background-50 whitespace-nowrap transition-colors"
              >
                #{w}
              </button>
            ))}
          </div>
          <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {articles.slice(0, 6).map((a) => (
              <ArticleCard key={a.id} article={a} layout="grid" />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}